import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useFieldArray, useForm } from 'react-hook-form';
import { FiChevronUp, FiTrash } from 'react-icons/fi';
import { SelectInput } from '../inputs';
import Langs from '../../utils/data/langs.json'
import { useDispatch, useSelector } from 'react-redux';
import { createOrder, getDayAvailability } from '../../store/appointment/appointmentActions';
import { dateToSlice } from '../../utils';
import moment from 'moment';

const AppointmentBox = ({ profile }) => {
    const { id: tutorProfileId } = useParams();
    const [value, onChange] = useState(moment().parseZone().add(1, "day").add(1, "hour").toDate());
    const [availableAppointments, setAvailableAppointments] = useState([])
    const [isSlicesLoading, setIsSlicesLoading] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const dispatch = useDispatch()
    const { currentProfile } = useSelector(state => state.profile);
    const [mobBoxOpen, setMobBoxOpen] = useState(false)
    useEffect(() => {
        if (!tutorProfileId) return;

        setIsSlicesLoading(true)
        dispatch(getDayAvailability({
            profile: tutorProfileId,
            day: moment(value).format("YYYY-MM-DD")
        }))
            .unwrap()
            .then(v => {
                const appointments = getValues("appointments");
                let slices = v.map((slice => {
                    return {
                        date: moment(slice.date),
                        string: dateToSlice(slice.date),
                        disabled: appointments.findIndex(k => moment(slice.date).diff(k.date) === 0) !== -1
                    };
                }))
                setAvailableAppointments(slices)
                setIsSlicesLoading(false)
            })
    }, [value, tutorProfileId])

    const appendDate = (slice) => {
        appointmentsAppend({ date: slice.date, text: moment(value).format("YYYY-MM-DD") + " " + dateToSlice(slice.date) })
        setAvailableAppointments((v) => v.map(appointment => { return appointment.date == slice.date ? { ...appointment, disabled: true } : appointment }))
    }

    const SessionTag = ({ slice, index }) => {
        return <div onClick={() => appendDate(slice)} className='bg-nt-grey text-white px-2 py-1 rounded cursor-pointer select-none'>
            {slice.string}
        </div>
    }

    let canTeach = profile.teach.map((v) => v.lang)
    const languages = Langs.map((lang) => { return { value: lang.value, label: lang.name } }).filter((l) => canTeach.includes(l.value))

    const { control, errors, watch, register, getValues } = useForm({
        defaultValues: {
            language: canTeach[0]
        }
    });

    const {
        fields: appointmentsFields,
        append: appointmentsAppend,
        remove: appointmentsRemove
    } = useFieldArray({
        control,
        name: "appointments",
    });

    const removeAppointment = (slice, i) => {
        setAvailableAppointments((v) => v.map(appointment => { return moment(appointment.date).diff(slice.date) === 0 ? { ...appointment, disabled: false } : appointment }))
        appointmentsRemove(i)
    }
    const curLang = watch("language")

    const checkout = () => {
        if (!tutorProfileId) return;

        setIsLoading(true)
        const selected = getValues("appointments");
        const data = selected.map((v => {
            return {
                tutor: tutorProfileId,
                student: currentProfile._id,
                date: moment(v.date).toISOString(),
            }
        }))

        dispatch(createOrder({ events: data, tutor: tutorProfileId, student: currentProfile._id, lang: getValues("language") }))
            .unwrap()
            .then((v) => {
                const url = typeof v === 'string' ? v.trim() : '';
                if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                    window.location.assign(url);
                } else {
                    console.error('Checkout: expected absolute payment URL from API, got:', v);
                }
            })
            .catch((err) => {
                console.error('Checkout failed', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }
    const { balance } = useSelector(state => state.wallet)
    const currentLangPricing = profile.teach.find((v) => v.lang == curLang)

    const getPriceColorClasses = (price, priceFive, priceTen) => {
        const allEqual = Number(price) === Number(priceFive) && Number(priceFive) === Number(priceTen)
        const greenClass = 'bg-green text-white font-semibold'
        if (allEqual) {
            return { course: greenClass, five: greenClass, ten: greenClass }
        }
        return {
            course: greenClass,
            five: 'bg-orange text-white font-semibold',
            ten: 'bg-nt-red text-white font-semibold',
        }
    }

    const Tarif = () => {
        if (!currentLangPricing) return null
        const { price, priceFive, priceTen } = currentLangPricing
        const colors = getPriceColorClasses(price, priceFive, priceTen)
        return <div className='mt-4 grid w-full gap-2 text-sm sm:grid-cols-3 sm:gap-3'>
            <p className={`rounded-lg px-3 py-2 ${colors.course}`}>{price}€ / 1 course</p>
            <p className={`rounded-lg px-3 py-2 ${colors.five}`}>{priceFive}€ / 5+ courses</p>
            <p className={`rounded-lg px-3 py-2 ${colors.ten}`}>{priceTen}€ / 10+ courses</p>
        </div>
    }
    
    const isFirstLesson = profile.isFirstLesson;
    const getTotal = () => {
        const prices = currentLangPricing;
        const countLessons = appointmentsFields.length
        var price = (countLessons >= 10 ? prices.priceTen : 
            countLessons >= 5 ? prices.priceFive : 
            prices.price)

        if(isFirstLesson && countLessons > 0)
            return <><span className='line-through text-nt-red'>{price * countLessons}</span> <span> {((price/2) + (price * (countLessons -1)))} €</span></>

        return <span>{price * countLessons} €</span>
    }
    return (
        <div className="mt-5 mb-20 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:mb-2 md:p-5">
            <div className='mb-5 flex flex-col gap-4 border-b border-slate-100 pb-4 md:flex-row md:items-end md:justify-between'>
                <div className='w-full'>
                    <h3 className='text-nt-blue'>Appointment booking</h3>
                    <p className='mt-1 text-sm text-nt-grey'>Choose a date, pick slots, and review your summary before checkout.</p>
                </div>
                <div className='w-full md:w-[280px]'>
                    <SelectInput
                        name={`language`}
                        label=""
                        errors={errors}
                        register={register}
                        control={control}
                        options={languages}
                        isSearchable={true}
                    />
                </div>
                <div className='md:hidden w-full'>
                    <Tarif />
                </div>
            </div>
            <div className='flex flex-col items-start gap-4 lg:flex-row'>
                <div className='w-full rounded-xl border border-slate-200 p-4 lg:w-2/6'>
                    <h5 className='mb-3 text-nt-blue'>Choose a date</h5>
                    <Calendar className={"nt-calendar nt-calendar--reschedule w-full"}
                        onChange={onChange}
                        view='month'
                        minDate={moment().parseZone().add(1, "day").add(1, "hour").toDate()}
                        maxDate={moment().parseZone().add(1, "year").add(1, "hour").toDate()}
                        value={value} />
                </div>
                <div className='w-full rounded-xl border border-slate-200 p-4 lg:w-2/6'>
                    <h5 className='mb-3 text-nt-blue'>Pick appointments</h5>
                    <div className='flex max-h-[300px] flex-wrap items-start gap-2 overflow-y-auto pr-1'>
                        {
                            isSlicesLoading ? <p className='text-sm text-nt-grey'>Loading available slices...</p> :
                                availableAppointments.length > 0 ? availableAppointments.map((v, i) => {
                                    if (!v.disabled)
                                        return <SessionTag slice={v} index={i} key={i} />
                                }) : <p className='text-sm text-nt-grey'>No available slices for selected date.</p>
                        }
                    </div>
                    
                </div>
                <div className='hidden w-full rounded-xl border border-slate-200 p-4 md:block lg:w-2/6'>
                    <h5 className='mb-3 text-nt-blue'>Selected appointments</h5>
                    <div className='flex flex-col justify-between md:min-h-[280px]'>
                        <div className='flex max-h-[240px] flex-wrap items-start gap-2 overflow-y-auto'>
                            {appointmentsFields.map((v, i) => {

                                return <div className='flex items-center gap-2 rounded-lg bg-nt-blue px-2.5 py-1.5 text-white' key={i}>
                                    {v.text} <span onClick={() => removeAppointment(v, i)}><FiTrash /></span>
                                </div>
                            })}
                            {appointmentsFields.length === 0 ? <p className='text-sm text-nt-grey'>No appointments selected yet.</p> : null}
                        </div>
                        <div className='mt-4 rounded-lg bg-slate-50 p-3'>
                            <div className='mb-2 flex items-end justify-between text-sm'>
                                <h5 className='text-slate-600'>Balance</h5>
                                <p className='font-medium text-slate-800'>{balance} €</p>
                            </div>
                            <div className='flex items-end justify-between text-sm'>
                                <h4 className='text-slate-700'>Total</h4>
                                <p className='font-semibold text-nt-blue'>{getTotal()}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className='hidden items-end justify-between gap-5 pt-4 md:flex'>
                <div className='flex-1'>
                    <Tarif />
                </div>
                <div className='w-full max-w-[280px]'>
                    <button disabled={appointmentsFields.length === 0 || isLoading} onClick={() => checkout()} className='w-full rounded-xl bg-nt-blue py-2.5 text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60'>
                        {isLoading ? "Loading..." : "Checkout"}
                    </button>
                </div>
            </div>

            <div className='fixed bottom-0 left-0 z-50 w-full rounded-t-2xl bg-nt-blue p-3 text-white shadow-2xl md:hidden'>
                <div onClick={() => setMobBoxOpen(x => !x)}>
                    <FiChevronUp className='absolute right-2' size={24} />
                </div>
                    <h5 className='mb-3'>{appointmentsFields.length} selected appointments</h5>
                    <div className={`flex h-full flex-col ${!mobBoxOpen ? "h-8" : "h-[80vh]"}`}>
                        <div className='flex flex-1 flex-col items-start gap-2 overflow-y-auto max-h-[58vh]'>
                            {appointmentsFields.map((v, i) => {

                                return <div className='flex items-center gap-2 rounded-lg bg-white px-2 py-1 text-nt-blue' key={i}>
                                    {v.text} <span onClick={() => removeAppointment(v, i)}><FiTrash /></span>
                                </div>
                            })}
                            {appointmentsFields.length === 0 ? <p className='text-sm text-white/80'>No appointments selected.</p> : null}
                        </div>
                        {mobBoxOpen === true && <div className='w-full self-end'>
                            <button disabled={appointmentsFields.length === 0 || isLoading} onClick={() => checkout()} className='mb-4 w-full rounded-xl bg-white py-2.5 text-nt-blue'>
                                {isLoading ? "Loading..." : "Checkout"}
                            </button>
                        </div>}
                        <div className='rounded-lg bg-white/10 p-2'>
                            <div className='flex items-end justify-between text-sm'>
                                <h5>Balance</h5>
                                <p>{balance} €</p>
                            </div>
                            <div className='flex items-end justify-between text-sm'>
                                <h4>Total</h4>
                                <p>{getTotal()}</p>
                            </div>
                        </div>
                    </div>
                </div>
        </div>
    )
}

export { AppointmentBox }